/**
 * Purchases. Every route here needs `cost.view` in addition to the purchase permission listed —
 * the server enforces that, this file just calls the endpoints; a caller without `cost.view`
 * never even sees the "Purchases" screen (gated in the nav/route the same as everywhere else).
 *
 * Drafts are edited in place (`updatePurchase`); once confirmed, a purchase is read-only — there
 * is no update endpoint for a confirmed one, only cancellation. Confirmation and serial capture
 * are separate calls: capturing serials for a line does not, by itself, apply the stock movement.
 */

import { api } from '../api.js';
import { purchaseListTag, purchaseTag } from '../tags.js';
import type {
  Paged,
  Purchase,
  PurchaseConfirmationResult,
  PurchaseId,
  PurchaseLineId,
  PurchaseSummary,
  ShopId,
  SupplierId,
  VariantId,
} from '../../types/api.js';

export type ListPurchasesParams = {
  readonly shopId?: ShopId | undefined;
  readonly supplierId?: SupplierId | undefined;
  readonly status?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly onlyWithOutstandingSerials?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type PurchaseLineInput = {
  readonly variantId: VariantId;
  readonly quantity: number;
  readonly unitCostKes: number;
};

export type PurchaseBody = {
  readonly shopId?: ShopId | undefined;
  readonly supplierId: SupplierId;
  readonly purchaseDate: string;
  readonly supplierInvoiceNo?: string | undefined;
  readonly note?: string | undefined;
  readonly lines: PurchaseLineInput[];
};

export const purchasesApi = api.injectEndpoints({
  endpoints: (build) => ({
    listPurchases: build.query<Paged<PurchaseSummary>, ListPurchasesParams>({
      query: (params) => ({ url: '/purchases', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: (result) => [
        purchaseListTag(),
        ...(result?.items ?? []).map((purchase) => purchaseTag(purchase.id)),
      ],
    }),

    getPurchase: build.query<Purchase, PurchaseId>({
      query: (id) => `/purchases/${id}`,
      providesTags: (_result, _error, id) => [purchaseTag(id)],
    }),

    createPurchase: build.mutation<PurchaseId, PurchaseBody>({
      query: (body) => ({ url: '/purchases', method: 'POST', body }),
      invalidatesTags: [purchaseListTag()],
    }),

    /** Drafts only — the server refuses this once a purchase is confirmed. */
    updatePurchase: build.mutation<void, { id: PurchaseId; body: PurchaseBody }>({
      query: ({ id, body }) => ({ url: `/purchases/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [purchaseTag(id), purchaseListTag()],
    }),

    confirmPurchase: build.mutation<
      PurchaseConfirmationResult,
      { id: PurchaseId; serialsByLine?: Readonly<Record<string, string[]>> | undefined }
    >({
      query: ({ id, serialsByLine }) => ({
        url: `/purchases/${id}/confirmation`,
        method: 'POST',
        body: serialsByLine === undefined ? {} : { serialsByLine },
      }),
      invalidatesTags: (_result, _error, { id }) => [purchaseTag(id), purchaseListTag()],
    }),

    cancelPurchase: build.mutation<void, { id: PurchaseId; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/purchases/${id}/cancellation`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { id }) => [purchaseTag(id), purchaseListTag()],
    }),

    /** Creates the serialized units; the stock movement for them lands separately, on confirm. */
    captureSerials: build.mutation<
      number,
      { purchaseId: PurchaseId; lineId: PurchaseLineId; serials: string[] }
    >({
      query: ({ purchaseId, lineId, serials }) => ({
        url: `/purchases/${purchaseId}/lines/${lineId}/serials`,
        method: 'POST',
        body: { serials },
      }),
      invalidatesTags: (_result, _error, { purchaseId }) => [purchaseTag(purchaseId)],
    }),
  }),
});

export const {
  useListPurchasesQuery,
  useGetPurchaseQuery,
  useCreatePurchaseMutation,
  useUpdatePurchaseMutation,
  useConfirmPurchaseMutation,
  useCancelPurchaseMutation,
  useCaptureSerialsMutation,
} = purchasesApi;
